// SKADE COP — kartlag/kildedata (frontend-bølge 1).
//
// Ekte bakgrunnskart operatører forventer, som lag i ÉN stil (ikke setStyle,
// som ville slettet spor/grafikk). Alle basiskart legges nederst som raster-lag
// og veksles på synlighet, så COP-overlaggene alltid ligger urørt oppå.
//
// Kilder (offisielle/åpne):
//  * Kartverket topografisk gråtone  — dempet, taktisk standard (symboler popper)
//  * Kartverket topografisk (farge)  — full topo
//  * Esri World Imagery              — ortofoto/satellitt
//  * Mørk (offline-tolerant)         — ren bakgrunn uten fliser (fallback)
//  * Terreng-relieff (hillshade)     — Terrarium-DEM, valgbart overlegg
//  * 3D-terreng                      — SAMME Terrarium-DEM, `map.setTerrain`
//
// 3D: eieren ville ha «Streets GL»-kartet for å kunne anta siktelinjer for
// tropper/OP-poster, radar og antennedekning. Streets GL er en egen WebGL2-motor
// uten innbyggings-API — å bytte til den betyr å kaste MapLibre og med det
// APP-6/milsymbol, kritiske objekter, MGRS-rutenett, terra-draw og ORBAT. Vi gir
// derfor samme KAPABILITET inne i MapLibre: ekte 3D-terreng her, og
// bygningsekstrudering + bygningshøyde-oppslag i `bygninger.ts`.
import type { Map as MlMap, SkySpecification } from 'maplibre-gl';
import { icon } from '../ui/icons.js';
import { bygningerFor } from './bygninger.js';

export interface BasemapDef {
  id: string;
  label: string;
  /** null = ingen fliser (ren bakgrunnsfarge — fungerer offline). */
  tiles: string | null;
  attribution: string;
  maxzoom?: number;
}

/** Bakgrunnsfargen bak/istedenfor fliser (GNÅ-mørk). */
export const BG_COLOR = '#0a0c0a';

// SKANDIA: valgfrie nasjonale topo-kart for Danmark/Sverige krever gratis token
// (Dataforsyningen / Lantmäteriet). Settes i .env (VITE_DK_TOKEN / VITE_SE_TOKEN);
// uten token faller vi tilbake på OSM/OpenTopoMap som dekker hele Norden likt.
const DK_TOKEN = (import.meta.env?.VITE_DK_TOKEN as string | undefined) ?? '';
const SE_TOKEN = (import.meta.env?.VITE_SE_TOKEN as string | undefined) ?? '';

// ── OFFLINE GRUNNKART PÅ EN AVSKÅRET KO-NODE (KART-01, §6 «bygg for feltet») ─
//
// Kortet: GNÅ (Flutter) løser offline kart med `flutter_map_pmtiles` — en fil
// BÅRET INN på enheten. COP kjørte til nå BARE mot cache.kartverket.no — i strid
// med §6 for et distrikt uten sambandslink til Kartverket. Samme fil-KONSEPT
// (mbtiles), ulik BÆRER: COP er en nettleser uten egen enhetsdisk, så filen
// bæres i stedet inn til HUBBEN COP allerede snakker med (samme origin), som
// leser den med samme generiske MBTiles-leser GEO-04 bygde
// (`services/hub/src/mbtiles.ts`) og serverer den på `/kart/grunnkart/{z}/{x}/{y}.png`
// (se den ruta i `services/hub/src/server.ts` for RBAC/scope-resonnementet).
//
// ── OPERATØRENS ARBEIDSFLYT PÅ EN AVSKÅRET KO-NODE ─────────────────────────
//  1. På en TILKOBLET maskin: hent/lag en Kartverket- eller OSM-mbtiles for
//     området (f.eks. et uttrekk fra Kartverkets WMTS eller `mb-util`/QGIS mot
//     et OSM-avtrekk). Samme fil kan gjenbrukes av GNÅs `flutter_map_pmtiles`.
//  2. Bær fila inn med minnepinne/datadiode — SAMME prosedyre som GEO-04s
//     GEOINT-fliser (se kommentaren over `POST /geoint/jobb/fliser` i
//     server.ts). Det er IKKE et HTTP-opplastingssteg her: en avskåret node
//     har ingen å laste opp TIL.
//  3. Legg fila på hubbens disk der den leter — `${SKADE_DATA_DIR}/grunnkart.mbtiles`
//     — ELLER sett `SKADE_GRUNNKART_MBTILES=/full/sti/til/fila.mbtiles` i
//     hubbens miljø (se `grunnkartMbtilesSti()` i `mbtiles.ts`).
//  4. Bygg/deploy COP med `VITE_OFFLINE_BASEMAP_URL` satt (typisk `/kart/grunnkart`,
//     samme origin som resten av COP-API-et). Da blir «Lokal (offline)» et
//     valgbart OG STANDARD grunnkart — se `DEFAULT_BASEMAP` under.
//  5. Uten steg 3–4 er COP UENDRET: Kartverket topo over nett, akkurat som før
//     KART-01 — dette er «også offline», ikke «erstatt online» (§1: ingen
//     stille degradering en operatør ikke kan se; feilvakta under varsler
//     uansett om selv «Lokal» skulle mangle fliser for et område).
const OFFLINE_BASEMAP_URL = ((import.meta.env?.VITE_OFFLINE_BASEMAP_URL as string | undefined) ?? '').trim();

/** Id-en «Lokal (offline)» får i BASEMAPS/velgeren når den er konfigurert. */
export const OFFLINE_BASEMAP_ID = 'lokal';

/**
 * Bygger basiskart-definisjonen for det lokale grunnkartet, gitt hub-
 * endepunktet fra config (eller tom streng = ikke konfigurert).
 *
 * Ren funksjon — testbar uten å stubbe `import.meta.env` (som er låst til
 * bygge-/modullastetidspunktet og ikke kan endres per test).
 */
export function offlineBasemapDef(url: string): BasemapDef | null {
  const u = url.trim().replace(/\/$/, '');
  if (!u) return null;
  return {
    id: OFFLINE_BASEMAP_ID,
    label: 'Lokal (offline)',
    tiles: `${u}/{z}/{x}/{y}.png`,
    attribution: 'Lokalt grunnkart (KO-node, se KART-01)',
    maxzoom: 19,
  };
}

/** Hvilket kart skal være FORVALGT, gitt om det lokale grunnkartet er
 *  konfigurert? Ren funksjon — se `offlineBasemapDef`. */
export function velgStandardBasiskart(offlineKonfigurert: boolean): string {
  // Er noden avskåret og en lokal fil er lagt inn, ER det feltets grunnkart —
  // operatøren skal ikke måtte huske å bytte bort fra et kart som ikke laster.
  return offlineKonfigurert ? OFFLINE_BASEMAP_ID : 'topo';
}

const OFFLINE_BASEMAP_DEF = offlineBasemapDef(OFFLINE_BASEMAP_URL);

export const BASEMAPS: BasemapDef[] = [
  {
    id: 'graatone',
    label: 'Gråtone (NO)',
    tiles: 'https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png',
    attribution: '© Kartverket',
    maxzoom: 18,
  },
  {
    id: 'topo',
    label: 'Topografisk (NO)',
    tiles: 'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png',
    attribution: '© Kartverket',
    maxzoom: 18,
  },
  {
    // ── SJØKART ────────────────────────────────────────────────────────────
    // Eieren ba om OpenCPN (opencpn.org) inn i SKADE. OpenCPN kan IKKE
    // bygges inn: det er et C++/wxWidgets-SKRIVEBORDSPROGRAM for Windows/Mac/
    // Linux/RPi/Android, uten innbyggings-API for nettlesere. Samme vurdering som
    // Streets GL lenger ned i denne fila.
    //
    // Men KAPABILITETEN eieren er ute etter — sjøkart for Oslofjorden, altså
    // dybder, farvann, navigasjonsmerker — er ikke bundet til programmet. Den
    // ligger i DATAENE, og Kartverket publiserer dem som åpne WMTS-fliser i
    // samme tjeneste vi alt bruker til topo. Ingen ny avhengighet, ingen ny
    // motor, ingen token.
    //
    // Verifisert mot tjenesten 2026-07-28, ikke antatt: flis z12/y1200/x2168
    // (Oslofjorden) svarer HTTP 200 med 56 646 byte PNG — mot 27 583 for topo på
    // samme rute. Den doble tettheten er dybdekoter, dybdetall og merker.
    // MERK: lagnavnet er `sjokartraster`. «sjokart» gir HTTP 400.
    id: 'sjokart',
    label: 'Sjøkart (NO)',
    tiles: 'https://cache.kartverket.no/v1/wmts/1.0.0/sjokartraster/default/webmercator/{z}/{y}/{x}.png',
    attribution: '© Kartverket (sjøkart)',
    maxzoom: 18,
  },
  {
    id: 'foto',
    label: 'Flyfoto',
    tiles: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Esri, Maxar, Earthstar Geographics',
    maxzoom: 19,
  },
  // SKANDIA-dekning (Sverige + Danmark + Norge likt) uten token:
  {
    id: 'opentopo',
    label: 'Topo (Norden)',
    tiles: 'https://a.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '© OpenTopoMap (CC-BY-SA), © OpenStreetMap-bidragsytere',
    maxzoom: 17,
  },
  {
    id: 'osm',
    label: 'OSM (Norden)',
    tiles: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap-bidragsytere',
    maxzoom: 19,
  },
  // Nasjonale topo-kart for DK/SE — kun med token (ellers utelatt).
  ...(DK_TOKEN
    ? [{
        id: 'dk',
        label: 'Skærmkort (DK)',
        tiles: `https://api.dataforsyningen.dk/topo_skaermkort_DAF/{z}/{x}/{y}.png?token=${DK_TOKEN}`,
        attribution: '© Styrelsen for Dataforsyning og Infrastruktur',
        maxzoom: 20,
      } as BasemapDef]
    : []),
  ...(SE_TOKEN
    ? [{
        id: 'se',
        label: 'Topowebb (SE)',
        tiles: `https://api.lantmateriet.se/open/topowebb-ccby/v1/wmts/token/${SE_TOKEN}/1.0.0/topowebb/default/3857/{z}/{y}/{x}.png`,
        attribution: '© Lantmäteriet (CC-BY)',
        maxzoom: 17,
      } as BasemapDef]
    : []),
  // Lokalt (offline) grunnkart — se KART-01-kommentaren over. Utelates fra
  // listen når ikke konfigurert, samme mønster som DK/SE-kartene rett over.
  ...(OFFLINE_BASEMAP_DEF ? [OFFLINE_BASEMAP_DEF] : []),
  {
    id: 'dark',
    label: 'Mørk',
    tiles: null,
    attribution: 'SKADE',
  },
];

// VURDERT OG FORKASTET: OpenFreeMap «liberty» (https://tiles.openfreemap.org/styles/liberty)
// som eget bakgrunnskart-valg. Den er en KOMPLETT MapLibre-stil, og å bruke den
// krever `map.setStyle()` — som sletter alle COP-lag (spor, CEP, symboler,
// tegninger, ORBAT). Alternativet, å kopiere inn ~120 liberty-lag manuelt, er
// dobbel båndbredde og et helt annet designsystem enn theme.css. Kartverket topo
// er dessuten riktigere for norsk bruk (stedsnavn, hyttefelt, stier).
// Vektorkilden brukes derfor KUN til bygninger (`bygninger.ts`) — der den gir noe
// rasterflisene fysisk ikke kan: høyde per bygning.

/** Standard bakgrunnskart. ENDRET 2026-07-27 på eierens ønske: han vil ikke ha
 *  gråtone som utgangspunkt, han vil ha 3D-kartet som førstevalg.
 *
 *  Valgt: Kartverket topografisk (FARGE). Begrunnelse:
 *   · Fargetopoen har hypsometriske høydefarger + innebygd fjellskygge, så den
 *     LESER som relieff når 3D-terrenget drar den opp. Gråtone flater ut i 3D —
 *     terrengformen forsvinner i det grå.
 *   · Riktig innhold for norsk bruk (stedsnavn, stier, hyttefelt, DTM-koter) —
 *     langt bedre enn OSM/OpenFreeMap i norsk utmark, som var alternativet.
 *   · Samme kilde/lisens/maxzoom som gråtone; ingen ny nettavhengighet.
 *  Gråtone er FORTSATT valgbart i kartlag-menyen (foretrekkes til kartgrafikk og
 *  tegning fordi symboler popper mer) — bare defaulten er byttet.
 *
 *  ENDRET IGJEN (KART-01): er et lokalt offline-grunnkart konfigurert
 *  (`VITE_OFFLINE_BASEMAP_URL`, se kommentaren over `OFFLINE_BASEMAP_URL`), er
 *  DET forvalget i stedet — en avskåret KO-node skal ikke starte på et
 *  kart som umiddelbart feiler mot cache.kartverket.no. Uten den
 *  miljøvariabelen er alt uendret: «topo», som før KART-01. */
const DEFAULT_BASEMAP = velgStandardBasiskart(!!OFFLINE_BASEMAP_DEF);

/** Gråtone beholdes tilgjengelig — dokumentert her så ingen «rydder» den bort. */
export const GRAATONE_BASEMAP = 'graatone';

const raster = (id: string) => `basemap-${id}`;

/** Legger alle basiskart-lag nederst i stilen. Kall FØR overlaggene legges til
 * (så de havner under). Returnerer id-en til laget som er synlig. */
export function addBasemapLayers(map: MlMap): string {
  for (const b of BASEMAPS) {
    if (!b.tiles) continue; // 'dark' er bare bakgrunnsfargen (allerede i stilen)
    map.addSource(raster(b.id), {
      type: 'raster',
      tiles: [b.tiles],
      tileSize: 256,
      maxzoom: b.maxzoom ?? 19,
      attribution: b.attribution,
    });
    map.addLayer({
      id: raster(b.id),
      type: 'raster',
      source: raster(b.id),
      layout: { visibility: b.id === DEFAULT_BASEMAP ? 'visible' : 'none' },
      // Gråtone/topo dempes litt i mørk COP så militærsymboler dominerer.
      paint: { 'raster-opacity': b.id === 'foto' ? 1 : 0.92 },
    });
  }
  meldBasiskartValg(map, DEFAULT_BASEMAP);
  return DEFAULT_BASEMAP;
}

/** Veksler synlig basiskart (skjuler de andre). */
export function setBasemap(map: MlMap, id: string) {
  for (const b of BASEMAPS) {
    if (!b.tiles) continue;
    const vis = b.id === id ? 'visible' : 'none';
    if (map.getLayer(raster(b.id))) map.setLayoutProperty(raster(b.id), 'visibility', vis);
  }
  // ÉN sannhet om hvilket grunnkart som gjelder: feilvakta under må vite hvilket
  // lag operatøren faktisk ser, ellers klager den på fliser fra kartet han nettopp
  // forlot (avbrutte forespørsler feiler i det laget skjules).
  meldBasiskartValg(map, id);
}

/** Sett gjennomsiktighet (0–1) for et basiskart-lag (til å dempe/tone ned). */
export function setBasemapOpacity(map: MlMap, id: string, opacity: number) {
  const o = Math.max(0, Math.min(1, opacity));
  if (map.getLayer(raster(id))) map.setPaintProperty(raster(id), 'raster-opacity', o);
}

// ── Et grunnkart som ikke laster skal ALDRI feile stille ───────────────────
//
// MÅLT 2026-07-28 i Chromium mot den ekte appen (ikke antatt):
//  · «Flyfoto» ER implementert og URL-en ER riktig. MapLibre henter
//    .../World_Imagery/MapServer/tile/{z}/{y}/{x}, og kilden svarte HTTP 200
//    image/jpeg (16 fliser, ~250 kB) og rendret ekte ortofoto over Oslofjorden.
//    MEN i samme 15-sekunders vindu svarte 2 av 18 fliser HTTP 503 — og det
//    fikk operatøren ALDRI vite. Et tomt/hullete kart var hele tilbakemeldingen.
//  · `map.on('error')` får sourceId + error.status for 401/403/429/5xx og for
//    nettverks-/CORS-brudd. Det dekker de fleste tilfellene.
//  · MEN HTTP 404 gir NULL error-hendelser (målt: 0 av 17). MapLibre behandler
//    404 som «denne flisa finnes ikke i settet», som er lovlig for glisne
//    flissett. Nettopp 404 er det en feilskrevet URL/lag-id gir — se
//    `sjokartraster`-kommentaren over: «ett tegn fra å være feil», og resultatet
//    er et tomt kart uten et ord til operatøren.
// DERFOR to mekanismer: hendelses-vakta for det MapLibre sier, og én
// verifiseringsflis per valg for det MapLibre tier om.

/** Hvor lenge samme grunnkart holder kjeft etter én melding. Ett valg gir 15–20
 *  flisforespørsler; uten demping ville operatøren fått 16 identiske varsler. */
export const BASISKART_FEIL_STILLE_MS = 20_000;

/** Kort, ærlig årsakstekst for en HTTP-status (null = ikke noe svar i det hele
 *  tatt: nett nede, DNS, CORS-avvisning). Ren funksjon → testbar. */
export function basiskartFeilaarsak(status?: number | null): string {
  if (status === 401 || status === 403) return `kilden avviste oss (HTTP ${status}) — mangler nøkkel eller tilgang`;
  if (status === 404) return 'flisene finnes ikke (HTTP 404) — URL eller lag-id er feil';
  if (status === 429) return 'kilden struper oss (HTTP 429)';
  if (typeof status === 'number' && status >= 500) return `kilden svarer ikke (HTTP ${status})`;
  if (typeof status === 'number') return `kilden svarte HTTP ${status}`;
  return 'ingen svar fra kilden (nett eller CORS)';
}

/** Hele operatørmeldingen. Én linje, sier HVA som er galt og HVA man kan gjøre. */
export function basiskartFeiltekst(id: string, status?: number | null): string {
  const navn = BASEMAPS.find((b) => b.id === id)?.label ?? id;
  return `Grunnkart «${navn}» lastet ikke: ${basiskartFeilaarsak(status)}. Velg en annen bakgrunn — «Mørk» virker uten nett.`;
}

/**
 * Filter + demper mellom flisfeil og operatøren.
 *
 * To ting den MÅ gjøre, som er lette å glemme:
 *  1. Bare klage på det AKTIVE grunnkartet. Når man bytter kart avbrytes flisene
 *     til det forrige, og de feiler — en melding om dem er ren støy og peker på
 *     feil kart.
 *  2. Bare klage ÉN gang per kart per `BASISKART_FEIL_STILLE_MS`.
 * Ren logikk (ingen DOM, ingen kart) → testes direkte.
 */
export class BasiskartFeilvakt {
  private aktiv = DEFAULT_BASEMAP;
  private sist = new Map<string, number>();

  constructor(private naa: () => number = () => Date.now()) {}

  /** Nytt grunnkart valgt: nullstill dempingen så et ekte problem sies med en gang. */
  settAktiv(id: string) {
    this.aktiv = id;
    this.sist.delete(id);
  }

  aktivtId(): string { return this.aktiv; }

  /** Returnerer meldingen som skal vises, eller null (ikke relevant / dempet). */
  registrer(sourceId: string | null | undefined, status?: number | null): string | null {
    if (!sourceId || !sourceId.startsWith('basemap-')) return null;
    const id = sourceId.slice('basemap-'.length);
    if (id !== this.aktiv) return null;
    const t = this.naa();
    const forrige = this.sist.get(id);
    if (forrige !== undefined && t - forrige < BASISKART_FEIL_STILLE_MS) return null;
    this.sist.set(id, t);
    return basiskartFeiltekst(id, status);
  }
}

/** Web-Mercator-flis for et punkt. Ren funksjon — brukes til å plukke ÉN
 *  representativ flis å verifisere kilden med (samme flis MapLibre selv ber om,
 *  så den ligger i nettleser-cachen og koster ikke en ekstra nedlasting). */
export function flisKoordinat(lon: number, lat: number, zoom: number): { z: number; x: number; y: number } {
  const z = Math.max(0, Math.min(22, Math.floor(zoom)));
  const n = 2 ** z;
  const klemtLat = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const rad = (klemtLat * Math.PI) / 180;
  const x = Math.floor(((((lon + 180) % 360) + 360) % 360) / 360 * n);
  const y = Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n);
  return { z, x: Math.min(n - 1, x), y: Math.max(0, Math.min(n - 1, y)) };
}

/** Fyller ut {z}/{x}/{y} i en flismal. null når kilden ikke har fliser («Mørk»). */
export function flisUrl(id: string, z: number, x: number, y: number): string | null {
  const b = BASEMAPS.find((q) => q.id === id);
  if (!b?.tiles) return null;
  const zz = Math.min(z, b.maxzoom ?? 19);
  const n = 2 ** zz;
  const skala = 2 ** (z - zz);
  return b.tiles
    .replace('{z}', String(zz))
    .replace('{x}', String(Math.min(n - 1, Math.floor(x / skala))))
    .replace('{y}', String(Math.min(n - 1, Math.floor(y / skala))));
}

interface BasiskartTilstand {
  vakt: BasiskartFeilvakt;
  meld?: (tekst: string) => void;
  koblet?: boolean;
}
// Per kart, ikke per modul: pop-out-vinduer har egne kart, og et varsel skal
// gjelde kartet det kom fra.
const BASISKART_TILSTAND = new WeakMap<MlMap, BasiskartTilstand>();

function tilstandFor(map: MlMap): BasiskartTilstand {
  let t = BASISKART_TILSTAND.get(map);
  if (!t) { t = { vakt: new BasiskartFeilvakt() }; BASISKART_TILSTAND.set(map, t); }
  return t;
}

/** Hvilket grunnkart er valgt nå? (Sannheten `setBasemap` skrev.) */
export function aktivtBasiskart(map: MlMap): string {
  return tilstandFor(map).vakt.aktivtId();
}

function meldBasiskartValg(map: MlMap, id: string) {
  const t = tilstandFor(map);
  t.vakt.settAktiv(id);
  if (t.meld) void verifiserBasiskart(map, id);
}

/**
 * Henter ÉN flis fra det valgte grunnkartet og sier det høyt hvis den ikke er OK.
 * Dette er den ENESTE måten å fange HTTP 404 på — MapLibre svelger den (målt).
 * Feiler stille internt hvis miljøet ikke har fetch (tester, eldre webview):
 * en manglende diagnose skal ikke velte kartet.
 */
async function verifiserBasiskart(map: MlMap, id: string) {
  const t = BASISKART_TILSTAND.get(map);
  if (!t?.meld || typeof fetch !== 'function') return;
  let url: string | null = null;
  try {
    const c = map.getCenter?.();
    const { z, x, y } = flisKoordinat(c?.lng ?? 10.6, c?.lat ?? 59.35, map.getZoom?.() ?? 9);
    url = flisUrl(id, z, x, y);
  } catch { return; }
  if (!url) return; // «Mørk» har ingen fliser å verifisere
  let status: number | null = null;
  try {
    const r = await fetch(url, { method: 'GET', cache: 'force-cache' });
    if (r.ok) return;
    status = r.status;
  } catch {
    status = null; // nett/CORS — også en ærlig feil
  }
  // Kan ha byttet kart mens vi ventet; vakta forkaster da meldingen selv.
  const tekst = t.vakt.registrer(`basemap-${id}`, status);
  if (tekst) t.meld(tekst);
}

/**
 * Kobler på feilrapportering for grunnkart. Idempotent — trygt å kalle flere
 * ganger (stil kan lastes på nytt). `meld` er operatørkanalen (alarmbanneret).
 */
export function overvaakBasiskart(map: MlMap, meld: (tekst: string) => void) {
  const t = tilstandFor(map);
  t.meld = meld;
  if (t.koblet) return;
  t.koblet = true;
  map.on('error', (e: unknown) => {
    const ev = e as { sourceId?: string; error?: { status?: number } };
    const tekst = t.vakt.registrer(ev.sourceId, ev.error?.status ?? null);
    if (tekst) meld(tekst);
  });
  // Verifiser også det kartet som ALLEREDE er valgt: svikter standardkartet ved
  // oppstart er det den viktigste meldingen av alle.
  void verifiserBasiskart(map, t.vakt.aktivtId());
}

/** Kilde-id for Terrarium-DEM-en som 3D-terrenget bruker. */
export const DEM_KILDE = 'dem';

/** EGEN DEM-kilde for hillshade-laget.
 *  MapLibre advarer eksplisitt i konsollen hvis samme raster-dem-kilde brukes til
 *  både `hillshade` og `setTerrain` («please consider using two separate sources
 *  to improve rendering quality») — de trenger ulik kant-/nabo-behandling av
 *  DEM-flisene. Vi holder dem derfor adskilt.
 *  Båndbredde: hillshade-laget er SKJULT som standard, og MapLibre henter ikke
 *  fliser for en kilde uten synlige lag — standardoppsettet (3D på, relieff av)
 *  laster altså bare ÉN DEM-kilde. Slår operatøren på relieff i tillegg dobles
 *  DEM-trafikken; med 3D-terreng på er relieff stort sett overflødig. */
export const DEM_RELIEFF_KILDE = 'dem-relieff';

/** Terrarium-DEM-spesifikasjonen (én sannhet, gjenbrukt av hillshade + 3D). */
export const DEM_SPEC = {
  type: 'raster-dem' as const,
  tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
  encoding: 'terrarium' as const,
  tileSize: 256,
  maxzoom: 14,
  attribution: 'Terreng: Mapzen/AWS Terrain Tiles',
};

/** Sørger for at en Terrarium-DEM-kilde finnes (idempotent). 3D-terreng kan slås
 *  på selv om `addHillshade` ikke er kalt ennå. */
export function sikreDemKilde(map: MlMap, id: string = DEM_KILDE): boolean {
  try {
    if (!map.getSource?.(id)) map.addSource(id, { ...DEM_SPEC } as never);
    return true;
  } catch {
    return false; // stilen er ikke lastet ennå
  }
}

/** Legger til et terreng-relieff-lag (skjult) fra en Terrarium-DEM. */
export function addHillshade(map: MlMap) {
  sikreDemKilde(map, DEM_RELIEFF_KILDE);
  map.addLayer({
    id: 'hillshade',
    type: 'hillshade',
    source: DEM_RELIEFF_KILDE,
    layout: { visibility: 'none' },
    paint: { 'hillshade-exaggeration': 0.5, 'hillshade-shadow-color': '#000000' },
  });
}

export function setHillshade(map: MlMap, on: boolean) {
  if (map.getLayer('hillshade')) map.setLayoutProperty('hillshade', 'visibility', on ? 'visible' : 'none');
}

// ── 3D-terreng (MapLibres innebygde, samme Terrarium-DEM) ──────────────────
//
// KOSTNAD: 3D-terreng laster DEM-fliser for hele utsnittet i tillegg til
// bakgrunnskartet, og rendrer et mesh. På en gradert linje er det merkbart.
// Derfor AV som standard, og kilden er DEN SAMME som hillshade bruker (ingen
// dobbel nedlasting når begge er på).
//
// MERK om lag/symboler: 3D-lagene legges av MapLibre under symbol-lagene, og
// symbol-lag dybdetestes ikke mot terrenget — spor, APP-6-symboler og etiketter
// blir derfor hevet med terrenget og synker IKKE ned i det. `bygninger.ts` legger
// i tillegg ekstruderingen FØR første symbol-lag, så bygg aldri dekker symboler.

/** Grenser og standardverdi for terreng-overdrivelse. 1.0 = ekte målestokk;
 *  norsk kystterreng leses lettere med litt overdrivelse. */
export const TERRENG_OVERDRIVELSE_MIN = 1;
export const TERRENG_OVERDRIVELSE_MAKS = 3;
export const TERRENG_OVERDRIVELSE_STANDARD = 1.5;

/** Kamerahelning som slås på når 3D aktiveres (0° = rett ned = ingen 3D å se). */
export const TERRENG_PITCH = 55;
/** Maks helning med 3D på (MapLibres standardtak er 60°). */
export const TERRENG_MAKS_PITCH = 80;
/** Maks helning i 2D-modus (MapLibres standard). */
export const FLAT_MAKS_PITCH = 60;

/** Klemmer overdrivelse inn i [MIN, MAKS] og tar imot søppel uten å velte. */
export function klemOverdrivelse(v: number): number {
  if (!Number.isFinite(v)) return TERRENG_OVERDRIVELSE_STANDARD;
  return Math.min(TERRENG_OVERDRIVELSE_MAKS, Math.max(TERRENG_OVERDRIVELSE_MIN, v));
}

/** `map.setTerrain`-spesifikasjonen for en gitt overdrivelse. */
export function terrengSpec(overdrivelse = TERRENG_OVERDRIVELSE_STANDARD): {
  source: string; exaggeration: number;
} {
  return { source: DEM_KILDE, exaggeration: klemOverdrivelse(overdrivelse) };
}

/** Mørk himmel/atmosfære i GNÅ-paletten (theme.css: --surface / --olive).
 *  Uten dette blir horisonten MapLibres lyseblå standard — helt feil i en
 *  natt-COP, og 3D ser «ødelagt» ut. */
export const HIMMEL_3D: SkySpecification = {
  'sky-color': '#0a0c0a',      // --surface
  'horizon-color': '#26302a',  // mellom --olive og --faintest
  'fog-color': '#141a16',
  'fog-ground-blend': 0.55,
  'horizon-fog-blend': 0.6,
  'sky-horizon-blend': 0.75,
  'atmosphere-blend': 0.8,
};

/** Flat modus: ingen synlig atmosfære (kartet er helten, jf. UX §2). */
export const HIMMEL_FLAT: SkySpecification = { 'atmosphere-blend': 0 };

export function settHimmel(map: MlMap, tredimensjonal: boolean) {
  try { map.setSky?.(tredimensjonal ? HIMMEL_3D : HIMMEL_FLAT); } catch { /* eldre MapLibre */ }
}

/** Er 3D-terreng aktivt? */
export function terrengPaa(map: MlMap): boolean {
  try { return !!map.getTerrain?.(); } catch { return false; }
}

/** Gjeldende overdrivelse (standardverdien når 3D er av). */
export function terrengOverdrivelse(map: MlMap): number {
  try {
    const e = map.getTerrain?.()?.exaggeration;
    return typeof e === 'number' ? e : TERRENG_OVERDRIVELSE_STANDARD;
  } catch {
    return TERRENG_OVERDRIVELSE_STANDARD;
  }
}

/**
 * Slår 3D-terreng av/på. Returnerer tilstanden ETTER kallet (false hvis
 * DEM-kilden ikke kunne legges til — da har vi ikke 3D, og skal ikke påstå det).
 *
 * Setter også kamerahelning og himmel, ellers ser operatøren ingen forskjell.
 */
export function settTerreng(map: MlMap, on: boolean, overdrivelse?: number): boolean {
  if (!on) {
    try { map.setTerrain?.(null); } catch { /* ingen terreng satt */ }
    settHimmel(map, false);
    try {
      map.easeTo?.({ pitch: 0, duration: 300 });
      map.setMaxPitch?.(FLAT_MAKS_PITCH);
    } catch { /* kart uten kamera (test) */ }
    return false;
  }
  if (!sikreDemKilde(map)) return false;
  const e = klemOverdrivelse(overdrivelse ?? terrengOverdrivelse(map));
  try {
    map.setTerrain?.(terrengSpec(e));
  } catch {
    return false;
  }
  settHimmel(map, true);
  try {
    map.setMaxPitch?.(TERRENG_MAKS_PITCH);
    // Bare vipp kameraet hvis operatøren står rett ovenfra — ellers respekter
    // helningen han allerede har valgt.
    if ((map.getPitch?.() ?? 0) < 15) map.easeTo?.({ pitch: TERRENG_PITCH, duration: 600 });
  } catch { /* kart uten kamera (test) */ }
  return true;
}

// ── Standardoppsett: 3D som FØRSTEVALG ────────────────────────────────────
//
// Eieren ba 2026-07-27 om at «3D-kartet» skal være førstevalget, ikke gråtone.
// MÅLT KOSTNAD (Chromium, 1440×900, Oslofjord z9 → Oslo z15, se rapport):
//   · Terrarium-DEM-en er den SAMME kilden hillshade allerede brukte, maxzoom 14,
//     256 px PNG. Den er billig: DEM-flisene er små og gjenbrukes av 3D-meshet.
//   · Bygninger (vektorfliser, z14+) er den DYRE delen — 100–330 kB per flis
//     inkludert alle andre OpenMapTiles-lag vi ikke bruker.
// DERFOR: 3D-terreng PÅ som standard, bygninger AV som standard (slås på med
// ett klikk når operatøren faktisk skal vurdere sikt i bebyggelse). Det er
// kompromisset — ikke «alt på» som ville gjort oppstart tung på en felt-PC.
export const TERRENG_STANDARD_PAA = true;
export const BYGNINGER_STANDARD_PAA = false;

/**
 * ÉN av/på-bryter for 3D-terreng, og den ligger i KARTVELGEREN (revisjonsfunn 25b).
 *
 * Før dette fantes «3D-terreng» på to steder: som overlegg i kartvelgeren og som
 * eget punkt i «Kart»-gruppa i bunn-docken. To brytere for én tilstand er en
 * løgn som venter på å skje — og duplikatet ga ingen ny kapabilitet.
 *
 * KARTVELGEREN VANT, av tre grunner:
 *  1. Nærhet til det operatøren tenker på. Tenker han «terreng», står han
 *     allerede i kartvelgeren: Relieff (terrengskygge), Bygninger (3D) og
 *     grunnkartet selv bor der. 3D-terreng er sisteleddet i samme tanke —
 *     HVORDAN bakgrunnen tegnes. Bunn-docken er verktøy man UTFØRER noe med
 *     (siktelinje, dekning, terrengskygge for et tidspunkt), ikke en
 *     tegne-innstilling.
 *  2. «Overdriv»-glidebryteren kan ikke følge med til docken. Den hører uløselig
 *     til bryteren (den er skjult til 3D er på), og docken har ingen plass til en
 *     glidebryter. Å beholde dock-punktet ville splittet én kontroll i to.
 *  3. Kartvelgeren speiler kartets FAKTISKE tilstand via `map.on('terrain')` —
 *     også når 3D slås på fra kommandopaletten eller av standardoppsettet. Den
 *     kan altså ikke havne ut av synk, uansett hvem som slo det på.
 *
 * Kommandopaletten beholder `terreng3d` som SØKBAR snarvei. Det er ikke et
 * duplikat i samme forstand: paletten har ingen egen av/på-visning å gå ut av
 * synk med, den kaller bare samme funksjon — og `terrain`-hendelsen oppdaterer
 * kartvelgeren umiddelbart etterpå.
 */
export const TERRENG_EIES_AV_KARTVELGEREN = true;

/**
 * Slår på standard 3D-oppsett. Kall EN gang etter `addBasemapLayers`/`addHillshade`
 * (dvs. etter at stilen er lastet). Idempotent, og trygg uten nett: klarer den
 * ikke å legge til DEM-kilden returnerer den false og kartet forblir 2D.
 */
export function aktiver3dStandard(map: MlMap): boolean {
  if (!TERRENG_STANDARD_PAA) return false;
  if (terrengPaa(map)) return true;
  const paa = settTerreng(map, true, TERRENG_OVERDRIVELSE_STANDARD);
  if (paa && BYGNINGER_STANDARD_PAA) bygningerFor(map).settPaa(true);
  return paa;
}

/** Justerer overdrivelsen live (no-op når 3D er av). */
export function settTerrengOverdrivelse(map: MlMap, overdrivelse: number): number {
  const e = klemOverdrivelse(overdrivelse);
  if (!terrengPaa(map)) return e;
  try { map.setTerrain?.(terrengSpec(e)); } catch { /* ignorer */ }
  return e;
}

/** Legger til OpenSeaMap sjømerker som gjennomsiktig overlegg (skjult). Dekker
 * hele Norden — nyttig i SKANDIA-samvirke (samme sjøkartlag NO/SE/DK). */
export function addSeamark(map: MlMap) {
  if (map.getSource('seamark')) return;
  map.addSource('seamark', {
    type: 'raster',
    tiles: ['https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 18,
    attribution: '© OpenSeaMap-bidragsytere',
  });
  map.addLayer({
    id: 'seamark', type: 'raster', source: 'seamark',
    layout: { visibility: 'none' },
    paint: { 'raster-opacity': 0.9 },
  });
}

export function setSeamark(map: MlMap, on: boolean) {
  if (map.getLayer('seamark')) map.setLayoutProperty('seamark', 'visibility', on ? 'visible' : 'none');
}

// ── Basiskart-velger (kompakt, sammenleggbar kontroll) ─────────────────────
// Én liten «Kartlag»-knapp som åpner en meny — ikke en permanent høy kolonne som
// dekker kartet/verktøylinja. Lukket som standard = rent kart.
//
// TO KONSEPTER, TO SEKSJONER (revisjonsfunn 25a): menyen inneholder to helt
// ulike slags valg, og de så tidligere identiske ut — samme knapp, samme rad,
// bare en tynn strek mellom. Operatøren kunne ikke se at «Sjøkart» ERSTATTER
// bakgrunnen mens «Sjømerker» LEGGES OPPÅ den.
//   · GRUNNKART: eksklusivt, velg ÉN  → radioknapper (○/●), egen overskrift.
//   · OVERLEGG:  kombinerbart, flere  → avkrysning (☐/☑), egen overskrift.
// Formen bærer regelen, så den ikke må forklares.
const SEKSJON_GRUNNKART = 'Grunnkart';
const SEKSJON_OVERLEGG = 'Overlegg';

/** Setter av/på-tilstand på et overlegg — klasse OG aria samtidig, så de aldri
 *  kan gli fra hverandre (en avkrysning som ser på ut men melder «off» til
 *  skjermleseren er like galt som en knapp som lyver visuelt). */
function merk(el: HTMLElement, paa: boolean) {
  el.classList.toggle('active', paa);
  el.setAttribute('aria-checked', String(paa));
}

/**
 * Et overlegg som eies UTENFOR kartlaget, men som hører hjemme i kartlag-menyen.
 *
 * Tråkorset og AO-rødmerkinga er eid av shell/verktøy, ikke av MapLibre-stilen —
 * men fagansvarlig leter etter dem HER, i menyen som heter «Kartlag». At de bodde
 * i en dock-popover med 17 oppføringer var grunnen til at de ikke ble funnet.
 * Denne kroken lar dem vises på riktig sted uten at kartlaget må eie tilstanden.
 */
export interface Ekstraoverlegg {
  navn: string;
  tittel: string;
  /** Nåværende tilstand — leses ved åpning, så avkryssinga aldri lyver. */
  er: () => boolean;
  /** Sett tilstanden. Returner den nye, faktiske tilstanden. */
  veksle: (paa: boolean) => boolean;
}

export class BasemapControl {
  /** Overlegg eid utenfor kartlaget (tråkors, AO-rødmerking …). */
  constructor(private readonly ekstra: readonly Ekstraoverlegg[] = []) {}

  private container!: HTMLElement;
  private body!: HTMLElement;
  private current = DEFAULT_BASEMAP;
  private hillshadeOn = false;
  private seamarkOn = false;
  private terrengOn = false;
  private open = false;

  onAdd(map: MlMap): HTMLElement {
    this.container = document.createElement('div');
    this.container.className = 'maplibregl-ctrl basemap-ctrl collapsed';

    const label = () => BASEMAPS.find((b) => b.id === this.current)?.label ?? 'Kartlag';

    // Sammenleggings-knapp (viser aktivt kartlag).
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'basemap-toggle';
    const glyph = document.createElement('span');
    glyph.className = 'basemap-toggle-glyph'; glyph.innerHTML = icon('map');
    const txt = document.createElement('span');
    txt.className = 'basemap-toggle-txt'; txt.textContent = label();
    toggle.append(glyph, txt);
    toggle.title = 'Velg kartlag';
    toggle.onclick = (e) => { e.stopPropagation(); this.setOpen(!this.open); };
    this.container.appendChild(toggle);

    // Menyinnhold (skjult til åpnet).
    this.body = document.createElement('div');
    this.body.className = 'basemap-body';
    this.container.appendChild(this.body);

    // ── Seksjon 1: GRUNNKART — velg ÉN ────────────────────────────────────
    const grunnGruppe = this.seksjon(SEKSJON_GRUNNKART, 'radiogroup');

    const radioer: HTMLButtonElement[] = [];
    for (const b of BASEMAPS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'basemap-btn basemap-radio' + (b.id === this.current ? ' active' : '');
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', String(b.id === this.current));
      btn.textContent = b.label;
      btn.onclick = () => {
        this.current = b.id;
        setBasemap(map, b.id);
        for (const el of radioer) {
          el.classList.toggle('active', el === btn);
          el.setAttribute('aria-checked', String(el === btn));
        }
        txt.textContent = label();
      };
      radioer.push(btn);
      grunnGruppe.appendChild(btn);
    }

    // Gjennomsiktighet hører til GRUNNKARTET (den demper bakgrunnen så
    // militærsymboler dominerer) — derfor i denne seksjonen, ikke blant overleggene.
    const opacityWrap = document.createElement('div');
    opacityWrap.className = 'basemap-opacity';
    const opacityTxt = document.createElement('span');
    const slider = document.createElement('input');
    slider.type = 'range'; slider.min = '20'; slider.max = '100'; slider.value = '92';
    slider.title = 'Hvor sterkt grunnkartet vises (dempes så symboler popper)';
    const visOpacity = () => { opacityTxt.textContent = `Synlighet ${slider.value} %`; };
    slider.oninput = () => { setBasemapOpacity(map, this.current, Number(slider.value) / 100); visOpacity(); };
    visOpacity();
    opacityWrap.append(opacityTxt, slider);
    grunnGruppe.appendChild(opacityWrap);

    // ── Seksjon 2: OVERLEGG — kombiner fritt ──────────────────────────────
    const overGruppe = this.seksjon(SEKSJON_OVERLEGG, 'group');

    const relief = this.avkrysning('Relieff', 'Terreng-skyggelegging av/på');
    relief.onclick = () => {
      this.hillshadeOn = !this.hillshadeOn;
      setHillshade(map, this.hillshadeOn);
      merk(relief, this.hillshadeOn);
    };
    overGruppe.appendChild(relief);

    const seamark = this.avkrysning('Sjømerker', 'OpenSeaMap sjømerker av/på (NO/SE/DK)');
    seamark.onclick = () => {
      this.seamarkOn = !this.seamarkOn;
      setSeamark(map, this.seamarkOn);
      merk(seamark, this.seamarkOn);
    };
    overGruppe.appendChild(seamark);

    // ── 3D (terreng + bygninger) — ENESTE av/på-punkt for 3D-terreng ──────
    // Se kommentaren over `TERRENG_EIES_AV_KARTVELGEREN` for hvorfor duplikatet i
    // bunn-docken er fjernet (revisjonsfunn 25b).
    const terreng = this.avkrysning('3D-terreng', 'Ekte 3D-terreng (Terrarium-DEM) — vipper kameraet. Tyngre på nett.');
    overGruppe.appendChild(terreng);

    // Overdrivelse: skjult til 3D er på (ingen kontroll som later som, UX §10).
    const exWrap = document.createElement('div');
    exWrap.className = 'basemap-opacity';
    const exTxt = document.createElement('span');
    const exSlider = document.createElement('input');
    exSlider.type = 'range';
    exSlider.min = String(TERRENG_OVERDRIVELSE_MIN * 10);
    exSlider.max = String(TERRENG_OVERDRIVELSE_MAKS * 10);
    exSlider.step = '1';
    exSlider.value = String(TERRENG_OVERDRIVELSE_STANDARD * 10);
    exSlider.title = 'Terreng-overdrivelse (1,0 = ekte målestokk)';
    const visEx = () => {
      const e = Number(exSlider.value) / 10;
      exTxt.textContent = `Overdriv ${e.toFixed(1)}×`;
    };
    exSlider.oninput = () => { settTerrengOverdrivelse(map, Number(exSlider.value) / 10); visEx(); };
    exSlider.addEventListener('vis', visEx); // synk fra kartets tilstand
    visEx();
    exWrap.append(exTxt, exSlider);
    exWrap.hidden = true;
    overGruppe.appendChild(exWrap);

    terreng.onclick = () => {
      settTerreng(map, !terrengPaa(map), Number(exSlider.value) / 10);
      this.visTerreng(map, terreng, exWrap, exSlider);
    };

    // 3D er PÅ som standard og slås på etter at stilen er lastet — altså ETTER
    // at denne kontrollen er lagt til. Speil derfor kartets FAKTISKE tilstand,
    // og følg `terrain`-hendelsen så knappen aldri lyver om hva som er på.
    this.visTerreng(map, terreng, exWrap, exSlider);
    map.on('terrain', () => this.visTerreng(map, terreng, exWrap, exSlider));

    // Bygninger (3D) — deler instans med dock/kommandopalett og siktelinje-modulen
    // via `bygningerFor(map)`, så alle ser samme av/på-tilstand.
    const bygg = this.avkrysning('Bygninger (3D)', 'OSM-bygninger ekstrudert (OpenFreeMap). Krever zoom 14+ og nett.');
    const status = document.createElement('div');
    status.className = 'basemap-opacity'; // gjenbrukt typografi (12px, --text-secondary)
    status.hidden = true;
    const lag = bygningerFor(map);
    const visStatus = () => {
      merk(bygg, lag.paa());
      status.textContent = lag.statusTekst();
      status.hidden = !lag.paa();
    };
    lag.onStateChange = visStatus;
    bygg.onclick = () => { lag.veksle(); visStatus(); };
    overGruppe.append(bygg, status);

    // Overlegg eid utenfor kartlaget. Avkryssinga LESES fra eieren hver gang
    // menyen åpnes (`setOpen`), så den kan ikke gli fra sannheten når samme lag
    // også slås av/på fra docken eller med hurtigtast.
    //
    // VIKTIG: IKKE kall `o.er()` her. Denne koden kjører i kontrollens onAdd —
    // altså MENS `new CopMap(...)` bygges i main.ts — og overleggseierne
    // (territory, dock) opprettes ETTER kartet. Et kall her leser en `const`
    // før initialisering og krasjet hele COP-en ved innlogging (TDZ-krasjen
    // 2026-08-04: «Cannot access 'ct' before initialization»). Startverdien
    // er derfor «av»; `setOpen` leser sannheten i det menyen åpnes.
    for (const o of this.ekstra) {
      const b = this.avkrysning(o.navn, o.tittel);
      merk(b, false);
      b.onclick = () => merk(b, o.veksle(!o.er()));
      this.ekstraKnapper.push({ knapp: b, kilde: o });
      overGruppe.appendChild(b);
    }

    // Klikk utenfor lukker menyen.
    this.outsideClick = (e: MouseEvent) => {
      if (this.open && !this.container.contains(e.target as Node)) this.setOpen(false);
    };
    document.addEventListener('click', this.outsideClick);

    return this.container;
  }

  /** Overskrift + gruppe for én seksjon. `rolle` gjør skillet lesbart også for
   *  skjermleser: radiogroup = velg én, group = kombiner fritt. */
  private seksjon(tittel: string, rolle: 'radiogroup' | 'group'): HTMLElement {
    const h = document.createElement('div');
    h.className = 'basemap-seksjon';
    h.id = `basemap-seksjon-${rolle}`;
    h.textContent = tittel;
    const g = document.createElement('div');
    g.className = 'basemap-gruppe';
    g.setAttribute('role', rolle);
    g.setAttribute('aria-labelledby', h.id);
    this.body.append(h, g);
    return g;
  }

  /** Overleggsknapp: avkrysning, ikke radiovalg. */
  private avkrysning(tekst: string, tittel: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'basemap-btn basemap-kryss';
    b.setAttribute('role', 'checkbox');
    b.setAttribute('aria-checked', 'false');
    b.textContent = tekst;
    b.title = tittel;
    return b;
  }

  /** Speiler kartets FAKTISKE 3D-tilstand i knapp + overdrivelses-slider, så
   *  menyen aldri påstår noe annet enn det kartet gjør (UX §10). */
  private visTerreng(map: MlMap, knapp: HTMLElement, sliderWrap: HTMLElement, input: HTMLInputElement) {
    this.terrengOn = terrengPaa(map);
    merk(knapp, this.terrengOn);
    sliderWrap.hidden = !this.terrengOn;
    if (this.terrengOn) {
      input.value = String(Math.round(terrengOverdrivelse(map) * 10));
      input.dispatchEvent(new Event('vis'));
    }
  }

  private outsideClick?: (e: MouseEvent) => void;
  /** Knappene for eksterne overlegg, så de kan friskes opp ved åpning. */
  private readonly ekstraKnapper: { knapp: HTMLButtonElement; kilde: Ekstraoverlegg }[] = [];

  private setOpen(v: boolean) {
    this.open = v;
    // Les tilstanden på nytt ved ÅPNING. Disse lagene kan ha blitt slått av/på fra
    // docken eller med hurtigtast siden sist, og en avkryssing som viser gammel
    // tilstand er verre enn ingen avkryssing.
    if (v) for (const { knapp, kilde } of this.ekstraKnapper) merk(knapp, kilde.er());
    this.container.classList.toggle('collapsed', !v);
    this.container.classList.toggle('open', v);
  }

  onRemove() {
    if (this.outsideClick) document.removeEventListener('click', this.outsideClick);
    this.container.remove();
  }
}
